import { useMemo } from 'react';
import { useErrorHandlerContext, useUserLanguage, useListNavigationStateContext } from '@/customHooks';
import { capitalizeFirstLetter, formatIdWithTooltip } from '@/helpers';
import { ContractFilterInput, GetContractsForListQuery, useGetContractsForListQuery } from '@/graphql/generated';
import { ButtonWithDropDownMenu } from '../molecules';
import { ContractStatusPill, LoadingSpinner } from '../atoms';
import { useNavigate } from 'react-router';
import { pathURL } from '@/components/constants/urls';
import textsData from '@/textData/ContractListTexts.json';

type ContractsForList = NonNullable<GetContractsForListQuery['contracts']>['items'][number]

type ContractListTableProps = {
  filters: ContractFilterInput;
};

const readableDate = (date: Date) => {
  const dateObj = new Date(date)
  return dateObj.toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

const dataFlatter = (data: ContractsForList[] | undefined) => {
  if (!data) return []

  return data.map((element) => {
    const unities = element.unities.map((unity) => capitalizeFirstLetter(unity.name)).join(', ')
    const monthlyChargeAmount = element.isAutoGenerateRentCharges === true ? element.monthlyChargeAmount?.toString() ?? 'No Applicable' : 'No Applicable'
    const propertyName = element.property?.name ? capitalizeFirstLetter(element.property.name) : 'No Applicable'
    const tenantName = element.tenant
      ? `${capitalizeFirstLetter(element.tenant.firstName)} ${capitalizeFirstLetter(element.tenant.lastName)}`
      : 'Sin Inquilino'

    return {
      id: element.id,
      propertyName,
      unity: unities,
      tenantName,
      startDate: readableDate(element.startDate),
      endDate: readableDate(element.finishDate),
      rentAmount: element.rentAmount?.toString(),
      status: element.statusOption,
      monthlyChargeAmount,
    }
  })
}

export const ContractListTable = ({ filters }: ContractListTableProps) => {
  const userLanguage = useUserLanguage()
  const texts = userLanguage === 'es' ? textsData.es : textsData.en

  const navigate = useNavigate()
  const { setListState } = useListNavigationStateContext()

  const { open: openErrorModal } = useErrorHandlerContext()

  const { data, error, loading } = useGetContractsForListQuery({
    variables: { input: filters },
  })

  const flattenedData = useMemo(
    () => (data ? dataFlatter(data.contracts?.items) : null),
    [data]
  )

  if (error) openErrorModal(error)

  if (loading) {
    return (
      <div className="flex justify-center py-6">
        <LoadingSpinner text />
      </div>
    )
  }

  if (!flattenedData) {
    return (
      <div className="py-6">
        <span>
          <b>{texts.noData}</b>
        </span>
      </div>
    )
  }

  return (
    <div className="py-6">
      <div className="overflow-x-auto rounded-xl grid grid-cols-[1fr_2fr_2fr_1fr_1fr_1fr_1fr_1fr_128px_64px] content-start w-full min-w-[900px]">
        <div className="contents">
          <div className="font-bold text-left p-[15px] border-t border-b border-gray-500 bg-white sticky top-0 z-10">
            {texts.contractId}
          </div>
          <div className="font-bold text-left p-[15px] border-t border-b border-gray-500 bg-white sticky top-0 z-10">
            {texts.property}
          </div>
          <div className="font-bold text-left p-[15px] border-t border-b border-gray-500 bg-white sticky top-0 z-10">
            {texts.unity}
          </div>
          <div className="font-bold text-left p-[15px] border-t border-b border-gray-500 bg-white sticky top-0 z-10">
            {texts.tenant}
          </div>
          <div className="font-bold text-left p-[15px] border-t border-b border-gray-500 bg-white sticky top-0 z-10">
            {texts.startDate}
          </div>
          <div className="font-bold text-left p-[15px] border-t border-b border-gray-500 bg-white sticky top-0 z-10">
            {texts.endDate}
          </div>
          <div className="font-bold text-left p-[15px] border-t border-b border-gray-500 bg-white sticky top-0 z-10">
            {texts.monthlyChargeAmount}
          </div>
          <div className="font-bold text-left p-[15px] border-t border-b border-gray-500 bg-white sticky top-0 z-10">
            {texts.rentAmount}
          </div>
          <div className="font-bold text-left p-[15px] border-t border-b border-gray-500 bg-white sticky top-0 z-10">
            {texts.status}
          </div>
          <div className="border-t border-b border-gray-500 bg-white sticky top-0 z-10"></div>
        </div>
        {flattenedData.map((contract) => {
          const { short, full } = formatIdWithTooltip(contract.id)
          return (
            <div key={contract.id} className="contents group">
              <div
                className="p-[15px] text-sm flex items-center border-b border-gray-200 min-h-[50px] group-odd:bg-[--color-background-2] group-even:bg-white"
                title={full}
              >
                {short}
              </div>
              <div className="p-[15px] text-sm flex items-center border-b border-gray-200 min-h-[50px] group-odd:bg-[--color-background-2] group-even:bg-white">
                {contract.propertyName}
              </div>
              <div className="p-[15px] text-sm flex items-center border-b border-gray-200 min-h-[50px] group-odd:bg-[--color-background-2] group-even:bg-white">
                {contract.unity}
              </div>
              <div className="p-[15px] text-sm flex items-center border-b border-gray-200 min-h-[50px] group-odd:bg-[--color-background-2] group-even:bg-white">
                {contract.tenantName}
              </div>
              <div className="p-[15px] text-sm flex items-center border-b border-gray-200 min-h-[50px] group-odd:bg-[--color-background-2] group-even:bg-white">
                {contract.startDate}
              </div>
              <div className="p-[15px] text-sm flex items-center border-b border-gray-200 min-h-[50px] group-odd:bg-[--color-background-2] group-even:bg-white">
                {contract.endDate}
              </div>
              <div className="p-[15px] text-sm flex items-center border-b border-gray-200 min-h-[50px] group-odd:bg-[--color-background-2] group-even:bg-white">
                {contract.monthlyChargeAmount}
              </div>
              <div className="p-[15px] text-sm flex items-center border-b border-gray-200 min-h-[50px] group-odd:bg-[--color-background-2] group-even:bg-white">
                {contract.rentAmount}
              </div>
              <div className="p-[15px] flex items-center border-b border-gray-200 min-h-[50px] group-odd:bg-[--color-background-2] group-even:bg-white">
                <ContractStatusPill status={contract.status} />
              </div>
              <div className="p-[15px] flex items-center justify-end border-b border-gray-200 min-h-[50px] group-odd:bg-[--color-background-2] group-even:bg-white">
                <ButtonWithDropDownMenu
                  options={[
                    {
                      item: 'Detalles',
                      action: () => {
                        setListState('contracts', {
                          currentPage: parseInt(filters.page || '1'),
                          filters,
                          selectedItemId: contract.id,
                        })

                        navigate(
                          `/${pathURL.USER}/${pathURL.CONTRACT}/${pathURL.TENANT}/${pathURL.DETAIL}`,
                          { state: { id: contract.id } }
                        )
                      },
                    },
                  ]}
                  position="left"
                />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
